/**
 * Statement of Account — /finance/reports/soa
 *
 * Customer SoA: AR invoices, credit notes, receipts, and balance.
 * Configurable date range. Shareable PDF.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { api } from '@/lib/api';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  FileText, Search, Send, Download, RefreshCw, BrainCircuit, ArrowRight, ReceiptText,
} from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { FinanceWorkspaceShell } from '../FinanceWorkspaceShell';
import { downloadAuthenticatedCsv, downloadAuthenticatedFile, fmtNumber, todayIso } from './report-utils';
import { useToast } from '@/lib/toast';
import { SoaPartyPicker } from './SoaPartyPicker';

interface SoaLine {
  date: string;
  reference: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
  type: 'invoice' | 'credit_note' | 'receipt' | 'adjustment';
}

interface SoaResult {
  reportType: string;
  view: 'ar' | 'ap' | 'combined';
  partyId: string;
  partyName: string;
  currency: string;
  from_date: string;
  to_date: string;
  opening_balance: number;
  closing_balance: number;
  totalDebits?: number;
  totalCredits?: number;
  lines: SoaLine[];
}

const TYPE_COLORS: Record<string, string> = {
  invoice:     'bg-blue-50 text-blue-700',
  credit_note: 'bg-green-50 text-green-700',
  receipt:     'bg-purple-50 text-purple-700',
  adjustment:  'bg-gray-50 text-gray-600',
};

export default function SoaPage() {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [result, setResult] = useState<SoaResult | null>(null);
  const [partyId, setPartyId] = useState('');
  const [partyLabel, setPartyLabel] = useState('');
  const [fromDate, setFrom] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10));
  const [toDate, setTo] = useState(todayIso());
  const [view, setView] = useState<'ar' | 'ap' | 'combined'>('ar');
  const [lineSearch, setLineSearch] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailNote, setEmailNote] = useState('');

  const loadStatement = useCallback(async (input: {
    view: 'ar' | 'ap' | 'combined';
    partyId: string;
    from: string;
    to: string;
  }) => {
    if (!input.partyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        view: input.view,
        partyId: input.partyId,
        from: input.from,
        to: input.to,
      });
      const res = await api.get<SoaResult>(`/finance/reports/soa?${params}`);
      setResult(res);
      setPartyLabel(res.partyName || input.partyId);
    } catch (error) {
      setResult(null);
      toast.error(error instanceof Error ? error.message : 'Statement generation failed');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const generate = useCallback(async () => {
    await loadStatement({ view, partyId, from: fromDate, to: toDate });
  }, [fromDate, loadStatement, partyId, toDate, view]);

  useEffect(() => {
    const presetPartyId = searchParams.get('partyId') ?? searchParams.get('customerId') ?? searchParams.get('vendorId');
    if (!presetPartyId) return;

    const presetView = (searchParams.get('view') as 'ar' | 'ap' | 'combined' | null) ?? 'ar';
    const presetFrom = searchParams.get('from') ?? new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
    const presetTo = searchParams.get('to') ?? todayIso();
    const presetPartyName = searchParams.get('partyName') ?? '';
    const autostart = searchParams.get('autostart') === '1';

    setView(presetView);
    setPartyId(presetPartyId);
    setPartyLabel(presetPartyName);
    setFrom(presetFrom);
    setTo(presetTo);

    if (autostart) {
      void loadStatement({ view: presetView, partyId: presetPartyId, from: presetFrom, to: presetTo });
    }
  }, [loadStatement, searchParams]);

  const handleExport = useCallback(async () => {
    if (!partyId) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({
        view,
        partyId,
        from: fromDate,
        to: toDate,
        format: 'csv',
      });
      await downloadAuthenticatedCsv(`/finance/reports/soa?${params}`, `soa-${view}-${partyId}-${toDate}.csv`);
      toast.success('SOA CSV 已下载');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '导出失败');
    } finally {
      setExporting(false);
    }
  }, [fromDate, partyId, toDate, toast, view]);

  const handlePdfDownload = useCallback(async () => {
    if (!partyId) return;
    setDownloadingPdf(true);
    try {
      const params = new URLSearchParams({
        view,
        partyId,
        from: fromDate,
        to: toDate,
        format: 'pdf',
      });
      const file = await downloadAuthenticatedFile(`/finance/reports/soa?${params}`, `soa-${view}-${partyId}-${toDate}.pdf`);
      toast.success(`已下载 ${file.fileName}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'PDF 导出失败');
    } finally {
      setDownloadingPdf(false);
    }
  }, [fromDate, partyId, toDate, toast, view]);

  const handleSendEmail = useCallback(async () => {
    if (!partyId || !recipientEmail.trim()) return;
    setSendingEmail(true);
    try {
      await api.post('/finance/reports/soa/send-email', {
        view,
        partyId,
        from: fromDate,
        to: toDate,
        recipientEmail: recipientEmail.trim(),
        note: emailNote.trim() || undefined,
      });
      toast.success('Statement email 已发送');
      setEmailDialogOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Email 发送失败');
    } finally {
      setSendingEmail(false);
    }
  }, [emailNote, fromDate, partyId, recipientEmail, toDate, toast, view]);

  const filteredLines = useMemo(
    () => (result?.lines ?? []).filter((line) => {
      if (!lineSearch.trim()) return true;
      const query = lineSearch.trim().toLowerCase();
      return line.reference.toLowerCase().includes(query)
        || line.description.toLowerCase().includes(query)
        || line.type.toLowerCase().includes(query);
    }),
    [lineSearch, result?.lines],
  );

  return (
    <FinanceWorkspaceShell
      title="Statement of Account"
      subtitle="Party statement with running balance and transaction history"
      Icon={ReceiptText}
      iconClassName="text-sky-600"
      moduleBadge="Report 5.3"
      domainBadge="Shareable statement"
      agentTitle="Statement Composer"
      agentSummary="This workspace builds a party statement using one consistent contract for AR, AP, or combined view, making the period movement and running balance easy to review or share."
      checklistTitle="SoA readiness"
      checklistStatus="Statement workflow"
      checklistNote="The page is now testable with correct `/finance/reports/soa` usage, selectable view, authenticated CSV export, and running-balance presentation."
      checklistItems={[
        'SoA now uses the canonical `partyId + view + from/to` contract.',
        'CSV export uses the same query and JWT-aware download path.',
        'AR / AP / combined statement modes are all testable from one page.',
      ]}
      quickActions={[
        { label: 'AR Aging', to: '/finance/reports/ar-aging' },
        { label: 'AP Aging', to: '/finance/reports/ap-aging' },
        { label: 'AR Workspace', to: '/finance/ar' },
        { label: 'Collections', to: '/finance/receipts', variant: 'secondary' },
      ]}
      headerActions={(
        result ? (
          <>
            <Button variant="outline" size="sm" onClick={handlePdfDownload} disabled={downloadingPdf || loading}>
              <FileText className={`h-4 w-4 mr-2 ${downloadingPdf ? 'animate-pulse' : ''}`} /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || loading}>
              <Download className={`h-4 w-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEmailDialogOpen(true)}>
              <Send className="h-4 w-4 mr-2" /> Email
            </Button>
          </>
        ) : null
      )}
    >

      {/* Params */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">View</label>
            <Select value={view} onValueChange={value => setView(value as 'ar' | 'ap' | 'combined')}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ar">AR</SelectItem>
                <SelectItem value="ap">AP</SelectItem>
                <SelectItem value="combined">Combined</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs text-muted-foreground mb-1 block">Party</label>
            <div className="grid gap-2 md:grid-cols-[minmax(220px,1fr)_minmax(0,1fr)]">
              <SoaPartyPicker
                value={partyId}
                label={partyLabel}
                placeholder={view === 'ap' ? 'Search vendor…' : 'Search customer…'}
                onSelect={(party) => {
                  setPartyId(party.id);
                  setPartyLabel(party.name);
                }}
              />
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Or paste UUID manually…"
                  className="pl-9"
                  value={partyId}
                  onChange={e => {
                    setPartyId(e.target.value);
                    if (partyLabel && e.target.value !== partyId) setPartyLabel('');
                  }}
                />
              </div>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Use search selection for normal testing, or paste UUID directly for exact lookup.</p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">From</label>
            <Input type="date" className="w-36" value={fromDate} onChange={e => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">To</label>
            <Input type="date" className="w-36" value={toDate} onChange={e => setTo(e.target.value)} />
          </div>
          <Button onClick={generate} disabled={loading || !partyId}>
            {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
            Generate
          </Button>
        </div>
      </Card>

      {result && (
        <>
          <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <BrainCircuit className="h-4 w-4 text-primary" />
                  Statement Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-4">
                <div className="rounded-xl border p-3">
                  <p className="text-xs text-muted-foreground">Party</p>
                  <p className="mt-1 text-sm font-semibold truncate">{result.partyName}</p>
                  <p className="text-xs text-muted-foreground">{result.partyId}</p>
                </div>
                <div className="rounded-xl border p-3">
                  <p className="text-xs text-muted-foreground">Opening</p>
                  <p className="mt-1 text-lg font-semibold">{fmtNumber(result.opening_balance)}</p>
                  <p className="text-xs text-muted-foreground">balance</p>
                </div>
                <div className="rounded-xl border p-3">
                  <p className="text-xs text-muted-foreground">Debits / Charges</p>
                  <p className="mt-1 text-lg font-semibold">{fmtNumber(result.totalDebits ?? 0)}</p>
                  <p className="text-xs text-muted-foreground">within period</p>
                </div>
                <div className="rounded-xl border p-3">
                  <p className="text-xs text-muted-foreground">Closing</p>
                  <p className={`mt-1 text-lg font-semibold ${result.closing_balance > 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {fmtNumber(result.closing_balance)}
                  </p>
                  <p className="text-xs text-muted-foreground">{result.currency}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Statement Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>View: <span className="font-medium text-foreground uppercase">{result.view}</span></p>
                <p>Period: <span className="font-medium text-foreground">{result.from_date} – {result.to_date}</span></p>
                <p>Credits / settlements: <span className="font-medium text-foreground">{fmtNumber(result.totalCredits ?? 0)}</span></p>
                <p>The transaction list below uses running balance order so you can verify every movement inside the selected range.</p>
              </CardContent>
            </Card>
          </div>

          <div className="relative max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search statement lines…" className="pl-9" value={lineSearch} onChange={e => setLineSearch(e.target.value)} />
          </div>

          <Card>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="hidden md:table-cell">Description</TableHead>
                    <TableHead className="hidden sm:table-cell">Type</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={6} className="text-xs text-muted-foreground">Opening balance</TableCell>
                    <TableCell className="text-right font-medium">{fmtNumber(result.opening_balance)}</TableCell>
                  </TableRow>
                  {filteredLines.map((l, i) => (
                    <TableRow key={i} className="border-b hover:bg-muted/20">
                      <TableCell className="text-xs">{l.date}</TableCell>
                      <TableCell className="font-mono text-xs">{l.reference}</TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{l.description}</TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge className={`text-xs ${TYPE_COLORS[l.type] ?? ''}`} variant="outline">{l.type.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{l.debit > 0 ? fmtNumber(l.debit) : '—'}</TableCell>
                      <TableCell className="text-right text-green-700">{l.credit > 0 ? fmtNumber(l.credit) : '—'}</TableCell>
                      <TableCell className="text-right font-medium">{fmtNumber(l.balance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/20 border-t-2">
                    <TableCell colSpan={4} className="font-semibold text-sm">Closing Balance</TableCell>
                    <TableCell colSpan={3} className="text-right font-bold text-base">{fmtNumber(result.closing_balance)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </Card>

          <div className="flex justify-end">
            <Button asChild variant="outline" size="sm">
              <Link to={view === 'ap' ? '/finance/ap' : '/finance/ar'}>
                Open {view === 'ap' ? 'AP' : 'AR'} workspace
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </>
      )}

      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Statement by Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl border bg-muted/20 p-3 text-sm">
              <p className="font-medium">{result?.partyName || partyLabel || partyId || 'Selected party'}</p>
              <p className="text-muted-foreground">{fromDate} – {toDate} · {view.toUpperCase()}</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Recipient Email</label>
              <Input value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="customer@example.com" type="email" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Note</label>
              <Textarea value={emailNote} onChange={e => setEmailNote(e.target.value)} placeholder="Optional note to include above the statement…" rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)} disabled={sendingEmail}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail || !recipientEmail.trim() || !partyId}>
              <Send className={`h-4 w-4 mr-2 ${sendingEmail ? 'animate-pulse' : ''}`} />
              Send Statement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FinanceWorkspaceShell>
  );
}
